// =============================================================================
// domain/analyticsPeriod.ts — Analytics' OWN period, not the global date filter.
// -----------------------------------------------------------------------------
// PORTED FROM `ANALYTICS_PERIODS` + `inAnalyticsPeriod` — semantics, not code.
//
// ── 🚨 ANALYTICS IGNORES THE GLOBAL DATE FILTER, DELIBERATELY ──
//
// The old client says so twice, in its own UI copy:
//
//   "Analytics uses its own period chips — the date filter is ignored on this tab."
//   "Analytics uses its own period chips (1W / 1M / 3M / YTD / 1Y / All). The
//    global date filter is disabled on this tab."
//
// …and it DISABLES the global date button while an analytics tab is open, rather
// than leaving it looking live. That matters more than it sounds: a filter that
// appears set but is ignored is worse than one that is visibly switched off,
// because the user reads every number through a scope that is not applied.
//
// ⚠ So a port must NOT wire the global range into Analytics. Doing so would look
// correct — the numbers would move when you changed the filter — and would be a
// different question answered than the one beta answers.
//
// ── THE PRESETS ARE "SINCE A CUTOFF", NOT A WINDOW ──
//
// `1W` means "the last 7 days up to now", with no upper bound. Only `custom` has
// two ends. Modelling the presets as a closed window would exclude a trade dated
// slightly in the future — which sounds impossible until a session-mode evening
// trade rolls its date forward past midnight.
// =============================================================================

import { dateToISO, isoToDate } from './session';

export interface AnalyticsPeriodDef {
  id: string;
  label: string;
  /** Days back from now. Absent for `ytd` and `all`. */
  days?: number;
}

/** Order and labels transcribed from `ANALYTICS_PERIODS`. */
export const ANALYTICS_PERIODS: readonly AnalyticsPeriodDef[] = [
  { id: '1w', label: '1W', days: 7 },
  { id: '1m', label: '1M', days: 30 },
  { id: '3m', label: '3M', days: 90 },
  { id: 'ytd', label: 'YTD' },
  { id: '1y', label: '1Y', days: 365 },
  { id: 'all', label: 'All' },
];

export type AnalyticsPeriodId = string;

export interface AnalyticsPeriod {
  id: AnalyticsPeriodId;
  /** Only meaningful when `id === 'custom'`. */
  customFrom?: string | null;
  customTo?: string | null;
}

export const DEFAULT_ANALYTICS_PERIOD: AnalyticsPeriod = { id: 'all' };

export function isAnalyticsPeriodId(v: unknown): boolean {
  return v === 'custom' || ANALYTICS_PERIODS.some((p) => p.id === v);
}

/**
 * The cutoff a preset implies — `null` for `all`, and for `custom`.
 *
 * ⚠ `today` is a PARAMETER (D26). Reading the clock here would make every
 * analytics number untestable and would silently re-scope the whole tab at
 * midnight under a user who has not touched anything.
 */
export function periodCutoff(period: AnalyticsPeriod, today: string): string | null {
  if (period.id === 'custom') return null;
  const def = ANALYTICS_PERIODS.find((p) => p.id === period.id);
  // ⚠ Unknown ids fall back to `all`, matching the old client's
  // `|| ANALYTICS_PERIODS[5]`. A stored id from an older build must not silently
  // become a one-week view.
  if (!def) return null;
  if (def.days) {
    const d = isoToDate(today);
    d.setDate(d.getDate() - def.days);
    return dateToISO(d);
  }
  if (def.id === 'ytd') return `${today.slice(0, 4)}-01-01`;
  return null;
}

/**
 * Is this EFFECTIVE trade date inside the period?
 *
 * ⚠ Presets are open-ended above (`>= cutoff`). Only `custom` bounds both sides,
 * and either of its ends may be absent.
 */
export function dateInAnalyticsPeriod(
  iso: string | null | undefined,
  period: AnalyticsPeriod,
  today: string,
): boolean {
  // ⚠ The old client defaults a missing date to '1970-01-01' rather than
  // dropping the trade, so an undated row survives `all` and falls outside every
  // preset. Ported literally: silently dropping it would make the counts
  // disagree with the trades table.
  const d = iso || '1970-01-01';

  if (period.id === 'custom') {
    if (period.customFrom && d < period.customFrom) return false;
    if (period.customTo && d > period.customTo) return false;
    return true;
  }

  const cutoff = periodCutoff(period, today);
  return !cutoff || d >= cutoff;
}

export function analyticsPeriodLabel(period: AnalyticsPeriod): string {
  if (period.id === 'custom') {
    return `${period.customFrom || 'start'} → ${period.customTo || 'today'}`;
  }
  return ANALYTICS_PERIODS.find((p) => p.id === period.id)?.label ?? 'All';
}

/** An equity point on the cumulative curve. */
export interface EquityPoint {
  date: string;
  time: string | null;
  equity: number;
  /** Negative-or-zero: distance below the running peak. */
  drawdown: number;
  pnl: number;
  id: string;
  contract: string | null;
}

export interface EquityTrade {
  id: unknown;
  date: string | null;
  timeIn: string | null;
  contract: string | null;
  /** The value in the active display unit. `null` drops the trade. */
  unitValue: number | null;
}

/**
 * The cumulative equity + drawdown curve, in the active display unit.
 *
 * ⚠ Trades with no value in the unit are SKIPPED, not zeroed — a stop-less trade
 * has no R and an uncatalogued contract has no tick size. Zeroing them would
 * flatten the curve across gaps that are really absences, and it is the same rule
 * `computeKpis` uses for its own drawdown so the two cannot disagree.
 *
 * ⚠ Peak seeds at 0, so an account that never goes green still reports drawdown
 * measured from flat rather than from its first losing trade.
 */
export function equityCurve(chronological: readonly EquityTrade[]): EquityPoint[] {
  const pts: EquityPoint[] = [];
  let cum = 0;
  let peak = 0;
  for (const t of chronological) {
    if (t.unitValue == null) continue;
    cum += t.unitValue;
    if (cum > peak) peak = cum;
    // ⚠ Explicit 0 at the peak. `-(peak - cum)` yields NEGATIVE ZERO there, which
    // formats fine today but is a real value that survives JSON, sorts oddly, and
    // fails an equality check against 0 — a trap laid for whatever reads this next.
    const dd = peak - cum;
    pts.push({
      date: t.date ?? '',
      time: t.timeIn,
      equity: cum,
      drawdown: dd === 0 ? 0 : -dd,
      pnl: t.unitValue,
      id: String(t.id),
      contract: t.contract,
    });
  }
  return pts;
}
